/**
 * Equipment specification sheets for the Parts tab:
 * public/data/equipment-specifications.json through react-query, the
 * analysis lookups (findBySubsystem, findByName) and badges (warranty, age
 * health, early failure) mapped to tones, plus the section ordering and the
 * sensor-to-item matching the panel needs.
 */
import {
  calculateAgeHealth,
  earlyFailureAlert,
  findByName as analysisFindByName,
  findBySubsystem as analysisFindBySubsystem,
  formatHistoryType,
  getSpecification,
  getWarrantyStatus,
  sortedServiceHistory,
  type AgeHealth,
  type EarlyFailure,
  type EquipmentSpec,
  type EquipmentSpecifications,
  type ServiceHistoryEntry,
  type WarrantyStatus,
} from '@/features/twin/analysis';
import type { Tone } from '@/components/ui/primitives';
import { useJsonFile } from './dataFiles';

export const SPECS_URL = '/data/equipment-specifications.json';

export type { EquipmentSpec, EquipmentSpecifications as SpecsFile, ServiceHistoryEntry as ServiceEntry };
export { sortedServiceHistory, formatHistoryType as serviceTypeLabel };

export function useEquipmentSpecs() {
  return useJsonFile<EquipmentSpecifications>(SPECS_URL);
}

export function getSpec(specs: EquipmentSpecifications | undefined, instanceId: string | null | undefined): EquipmentSpec | null {
  return specs && instanceId ? getSpecification(specs, instanceId) : null;
}

export function findBySubsystem(specs: EquipmentSpecifications | undefined, subsystemId: string): EquipmentSpec | null {
  return specs ? analysisFindBySubsystem(specs, subsystemId) : null;
}

export function findByName(specs: EquipmentSpecifications | undefined, name: string | null | undefined): EquipmentSpec | null {
  return specs ? analysisFindByName(specs, name) : null;
}

const WARRANTY_TONE: Record<WarrantyStatus['cssClass'], Tone> = { active: 'nominal', expiring: 'caution', expired: 'warning', unknown: 'neutral' };
const HEALTH_TONE: Record<AgeHealth['status'], Tone> = { good: 'nominal', fair: 'caution', warning: 'caution', critical: 'warning', unknown: 'neutral' };

export function warrantyStatus(spec: EquipmentSpec | null, nowMs: number): WarrantyStatus & { tone: Tone } {
  const w = getWarrantyStatus(spec, nowMs);
  return { ...w, tone: WARRANTY_TONE[w.cssClass] };
}

export function ageHealth(spec: EquipmentSpec | null, nowMs: number): AgeHealth & { tone: Tone } {
  const h = calculateAgeHealth(spec, nowMs);
  return { ...h, tone: HEALTH_TONE[h.status] };
}

/** The early failure alert (age health below 50 and before the expected end of life). */
export function earlyFailure(spec: EquipmentSpec | null, nowMs: number): EarlyFailure | null {
  return earlyFailureAlert(spec, nowMs);
}

export const SPEC_SECTION_ORDER = ['electrical', 'rf', 'mechanical', 'environmental', 'network', 'performance', 'acoustic', 'materials', 'lubrication'];

export const SPEC_SECTION_LABEL: Record<string, string> = {
  electrical: 'Electrical',
  rf: 'RF performance',
  mechanical: 'Mechanical',
  environmental: 'Environmental',
  network: 'Network',
  performance: 'Performance',
  acoustic: 'Acoustic',
  materials: 'Materials',
  lubrication: 'Lubrication',
  structural: 'Structural',
  display: 'Display',
  dataRates: 'Data rates',
  fec_modes: 'FEC modes',
  optical: 'Optical',
  protection: 'Protection',
  compute: 'Compute',
  storage: 'Storage',
  control: 'Control',
  interfaces: 'Interfaces',
  tracking: 'Tracking',
  processing: 'Processing',
  switching: 'Switching',
};

/** Sections in the documented order, then any others the instance carries. */
export function specSections(spec: EquipmentSpec): string[] {
  const keys = Object.keys(spec.technicalSpecs ?? {});
  const ordered = SPEC_SECTION_ORDER.filter((k) => keys.includes(k));
  return [...ordered, ...keys.filter((k) => !ordered.includes(k))];
}

/** "camelCase_key" to "Camel case key". */
export function specKeyLabel(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Nested spec objects flattened to [label, value] rows ("Voltage, tolerance" for nested keys). */
export function flattenSpec(obj: Record<string, unknown> | undefined, prefix = ''): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  for (const [key, value] of Object.entries(obj ?? {})) {
    const label = prefix ? `${prefix}, ${specKeyLabel(key).toLowerCase()}` : specKeyLabel(key);
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      rows.push(...flattenSpec(value as Record<string, unknown>, label));
    } else if (Array.isArray(value)) {
      rows.push([label, value.map(String).join(', ')]);
    } else {
      rows.push([label, String(value)]);
    }
  }
  return rows;
}

/** Sensor ids on a spec ("lna_gain", "acu_cabinet_temp") matched to a subsystem's data item ids. */
export function matchSensorToItem(sensorId: string, itemIds: string[]): string | null {
  if (itemIds.includes(sensorId)) return sensorId;
  const stripped = sensorId.replace(/^(lna|hpa|acu|modem|ft|sa|if|ifl|antenna|decoder|beacon|tracking|switch|matrix|psu|storage|waveguide|feed_horn|fan)_/, '');
  if (itemIds.includes(stripped)) return stripped;
  const loose = stripped.replace(/_/g, '');
  for (const id of itemIds) {
    if (id.replace(/_/g, '') === loose) return id;
  }
  for (const id of itemIds) {
    const candidate = id.replace(/_/g, '');
    if (loose.length >= 4 && (candidate.startsWith(loose) || loose.startsWith(candidate))) return id;
  }
  return null;
}
